import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login, admin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (admin) navigate("/admin", { replace: true });
  }, [admin, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Nina.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-20"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md surface p-10">
        <Link
          to="/"
          className="link-gold inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-8"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <Lock size={18} style={{ color: "var(--gold)" }} />
          <p className="eyebrow">Admin</p>
        </div>
        <h1 className="font-serif text-4xl tracking-tighter font-light mb-8">
          Sign in.
        </h1>
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="eyebrow">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 mt-2"
              data-testid="admin-email"
            />
          </div>
          <div>
            <Label htmlFor="password" className="eyebrow">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 mt-2"
              data-testid="admin-password"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="btn-gold w-full py-6 text-xs rounded-none"
            data-testid="admin-login-submit"
          >
            {busy ? "Signing in…" : "Enter"}
          </Button>
        </form>
      </div>
    </div>
  );
}
